import { useEffect, useRef, useState } from 'react';
import { resolveLocationLabel } from '../services/unlocodeService';

const normalizeList = value => (Array.isArray(value) ? value : []);

const buildSignature = list => {
  if (!Array.isArray(list) || !list.length) {
    return '[]';
  }
  return JSON.stringify(
    list.map(item => {
      if (!item || typeof item !== 'object') {
        return item;
      }
      return {
        id: item.id ?? null,
        pickup: item.pickupLocation ?? null,
        dropoff: item.dropoffLocation ?? null,
        location: item.location ?? null,
        updated: item.updated ?? item.updatedAt ?? null,
      };
    })
  );
};

export default function useActivityMarkers(requestItems = [], publicationItems = []) {
  const [markers, setMarkers] = useState([]);
  const [loading, setLoading] = useState(false);
  const locationCacheRef = useRef(new Map());

  const requestList = normalizeList(requestItems);
  const publicationList = normalizeList(publicationItems);
  const requestSignature = buildSignature(requestList);
  const publicationSignature = buildSignature(publicationList);

  const requestsRef = useRef({ signature: '[]', list: [] });
  const publicationsRef = useRef({ signature: '[]', list: [] });

  if (requestsRef.current.signature !== requestSignature) {
    requestsRef.current = { signature: requestSignature, list: requestList };
  }

  if (publicationsRef.current.signature !== publicationSignature) {
    publicationsRef.current = { signature: publicationSignature, list: publicationList };
  }

  useEffect(() => {
    let cancelled = false;
    const cache = locationCacheRef.current;
    const aggregator = new Map();

    const addEntry = (entry, meta) => {
      if (!entry || entry.latitude == null || entry.longitude == null) {
        return;
      }
      const key = entry.locode || `${entry.latitude},${entry.longitude}`;
      let bucket = aggregator.get(key);
      if (!bucket) {
        bucket = {
          key,
          locode: entry.locode || '',
          name: entry.nameWoDiacritics || entry.name || '',
          display: entry.display || entry.locode || '',
          country: entry.country || '',
          latitude: entry.latitude,
          longitude: entry.longitude,
          items: [],
          counts: { request: 0, publication: 0 },
        };
        aggregator.set(key, bucket);
      }
      bucket.items.push(meta);
      bucket.counts[meta.type] = (bucket.counts[meta.type] || 0) + 1;
    };

    const scheduleResolution = (label, meta) => {
      const trimmed = String(label || '').trim();
      if (!trimmed) {
        return Promise.resolve();
      }

      if (cache.has(trimmed)) {
        addEntry(cache.get(trimmed), meta);
        return Promise.resolve();
      }

      return resolveLocationLabel(trimmed)
        .then(entry => {
          cache.set(trimmed, entry);
          addEntry(entry, meta);
        })
        .catch(error => {
          console.warn('No se pudo resolver la ubicacion del feed', trimmed, error);
          cache.set(trimmed, null);
        });
    };

    const aggregateMarkers = async () => {
      setLoading(true);

      try {
        const jobs = [];

        const currentRequests = requestsRef.current.list;
        const currentPublications = publicationsRef.current.list;

        currentRequests.forEach(item => {
          if (!item) {
            return;
          }
          if (item.pickupLocation) {
            jobs.push({
              label: item.pickupLocation,
              meta: {
                type: 'request',
                id: item.id,
                title: item.title,
                companyName: item.company?.name || '',
                direction: 'pickup',
                locationLabel: item.pickupLocation,
                updatedAt: item.updated,
                serviceTypeLabel: item.serviceTypeLabel,
              },
            });
          }
          if (item.dropoffLocation) {
            jobs.push({
              label: item.dropoffLocation,
              meta: {
                type: 'request',
                id: item.id,
                title: item.title,
                companyName: item.company?.name || '',
                direction: 'dropoff',
                locationLabel: item.dropoffLocation,
                updatedAt: item.updated,
                serviceTypeLabel: item.serviceTypeLabel,
              },
            });
          }
        });

        currentPublications.forEach(item => {
          if (!item) {
            return;
          }
          if (item.location) {
            jobs.push({
              label: item.location,
              meta: {
                type: 'publication',
                id: item.id,
                title: item.title,
                companyName: item.companyName,
                locationLabel: item.location,
                updatedAt: item.updated,
                typeLabel: item.typeLabel,
                numericId: item.numericId,
              },
            });
          }
        });

        for (const job of jobs) {
          if (cancelled) {
            break;
          }
          const { label, meta } = job;
          try {
            await scheduleResolution(label, meta);
          } catch (jobError) {
            console.warn('No se pudo resolver una ubicacion para el mapa', label, jobError);
          }
        }

        if (cancelled) {
          return;
        }

        const nextMarkers = Array.from(aggregator.values()).map(bucket => ({
          ...bucket,
          total: bucket.items.length,
        }));

        nextMarkers.sort((a, b) => b.total - a.total);
        setMarkers(nextMarkers);
      } catch (error) {
        console.warn('No se pudieron preparar los marcadores del mapa', error?.message || error);
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    aggregateMarkers();

    return () => {
      cancelled = true;
    };
  }, [requestSignature, publicationSignature]);

  return { markers, loading };
}
